import { useState, useEffect, useCallback, useRef } from 'react';
import TopNav from './TopNav';
import { useToast } from '../Toast';
import { compressImage } from '../imageCompress';

const API = process.env.REACT_APP_API_URL || '';

// Incident Reports — a staged record: Report → Supervisor Investigation → HR
// Follow-up. Filers file + see their own; overseeing managers see + investigate
// their team's; HR + admins see all and do every stage. Mobile-first.

const REPORT_TYPES = [
  { key: 'general',  label: 'General' },
  { key: 'customer', label: 'Customer / Guest' },
  { key: 'employee', label: 'Employee' },
];
const TYPE_LABEL = Object.fromEntries(REPORT_TYPES.map(t => [t.key, t.label]));

const GENERAL_CATEGORIES = [
  { key: 'equipment',   label: 'Equipment / Facility' },
  { key: 'security',    label: 'Security' },
  { key: 'theft',       label: 'Theft / Loss' },
  { key: 'food_safety', label: 'Food Safety' },
  { key: 'other',       label: 'Other' },
];
const CAT_LABEL = Object.fromEntries(GENERAL_CATEGORIES.map(c => [c.key, c.label]));

const LOCATIONS = [
  { key: 'midtown', label: 'Midtown' }, { key: 'northside', label: 'Northside' },
  { key: 'power_mill', label: 'Power Mill' }, { key: 'tampa', label: 'Tampa' }, { key: 'other', label: 'Other / Offsite' },
];
const LOC_LABEL = Object.fromEntries(LOCATIONS.map(l => [l.key, l.label]));

const MATTER_TYPES = [
  { key: 'injury', label: 'Injury' }, { key: 'illness', label: 'Illness' }, { key: 'near_miss', label: 'Near miss' },
];
const MATTER_LABEL = Object.fromEntries(MATTER_TYPES.map(m => [m.key, m.label]));

const HR_SEVERITY = [
  { key: 'death', label: 'Death' }, { key: 'lost_time', label: 'Lost Time' },
  { key: 'dr_visit', label: 'Doctor Visit Only' }, { key: 'first_aid', label: 'First Aid Only' }, { key: 'near_miss', label: 'Near Miss' },
];
const SEVERITY_LABEL = Object.fromEntries(HR_SEVERITY.map(s => [s.key, s.label]));

const STATUSES = [
  { key: 'open', label: 'Open', badge: 'bg-red-500/20 text-red-300 border-red-500/30' },
  { key: 'reviewing', label: 'Under Review', badge: 'bg-amber-500/20 text-amber-300 border-amber-500/30' },
  { key: 'resolved', label: 'Resolved', badge: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' },
];
const STATUS_LABEL = Object.fromEntries(STATUSES.map(s => [s.key, s.label]));
const STATUS_BADGE = Object.fromEntries(STATUSES.map(s => [s.key, s.badge]));

// Prefill the employee "Job title" from the filer's role.
const ROLE_LABELS = {
  admin: 'Admin', bar_manager: 'Bar Manager', bartender: 'Bartender', barista: 'Barista',
  coffee_manager: 'Coffee Manager', production: 'Production', sales: 'Sales', hr: 'HR',
  kitchen_manager: 'Kitchen Manager', cook: 'Cook', distilling: 'Distilling',
};

function parseDate(s) { return s ? new Date(`${s}T12:00:00`) : null; }
function fmtDate(s) { const d = parseDate(s); return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : ''; }
function fmtDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
const yesNo = (v) => v === true ? 'Yes' : v === false ? 'No' : '';

const FIELD = 'w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none';
const LBL = 'block text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1';

function IncidentPhoto({ id }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = false, obj = null;
    fetch(`${API}/api/incidents/${id}/photo`, { credentials: 'include' })
      .then(r => r.ok ? r.blob() : null)
      .then(b => { if (b && !revoked) { obj = URL.createObjectURL(b); setUrl(obj); } }).catch(() => {});
    return () => { revoked = true; if (obj) URL.revokeObjectURL(obj); };
  }, [id]);
  return url ? <img src={url} alt="Incident" className="rounded-lg border border-gray-700 max-h-64 w-auto" /> : null;
}

// Three-state Yes/No control storing '', 'true', 'false'.
function YesNo({ value, onChange }) {
  return (
    <div className="flex gap-2">
      {[['true', 'Yes'], ['false', 'No']].map(([v, label]) => (
        <button key={v} type="button" onClick={() => onChange(value === v ? '' : v)}
          className={`px-3 py-1.5 rounded-lg text-sm border transition ${value === v ? 'bg-orange-500 text-white border-orange-500' : 'bg-gray-900 text-gray-300 border-gray-700 hover:border-gray-500'}`}>
          {label}
        </button>
      ))}
    </div>
  );
}

// Read-only labeled value (renders nothing when empty).
function Field({ label, children }) {
  const empty = children === null || children === undefined || children === '' || (typeof children === 'string' && !children.trim());
  if (empty) return null;
  return (
    <div>
      <div className="text-gray-500 text-xs font-semibold uppercase tracking-wider">{label}</div>
      <div className="text-white text-sm whitespace-pre-wrap">{children}</div>
    </div>
  );
}

// ── Filing form (type-driven) ─────────────────────────────────────────────────
function ReportModal({ user, onClose, onSaved }) {
  const toast = useToast();
  const today = new Date().toLocaleDateString('en-CA');
  const [type, setType] = useState('general');
  const [f, setF] = useState({
    incident_date: today, incident_time: '', location: '', category: '', description: '',
    witnesses: '', people_involved: '', action_taken: '', additional_notes: '',
    // customer
    customer_name: '', customer_phone: '', customer_email: '', receipt_order_no: '', payment_method: '',
    // emergency
    injury_occurred: '', ems_called: '', ems_arrival_time: '', police_called: '', police_report_no: '',
    // employee
    employee_matter_type: 'injury', job_title: ROLE_LABELS[user?.role] || '', supervisor_name: '', supervisor_notified: '',
    specific_location: '', activity_at_time: '', body_parts: '', prevention_suggestion: '',
    saw_doctor: '', doctor_name: '', doctor_phone: '', prior_injury: '',
  });
  const [photo, setPhoto] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!f.incident_date) { toast.error('Please set the incident date.'); return; }
    if (!f.description.trim()) { toast.error('Please describe what happened.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('report_type', type);
      const base = ['incident_date', 'incident_time', 'location', 'description', 'witnesses', 'action_taken', 'additional_notes'];
      const byType = {
        general: ['category', 'people_involved'],
        customer: ['people_involved', 'customer_name', 'customer_phone', 'customer_email', 'receipt_order_no', 'payment_method',
          'injury_occurred', 'ems_called', 'ems_arrival_time', 'police_called', 'police_report_no'],
        employee: ['employee_matter_type', 'job_title', 'supervisor_name', 'supervisor_notified', 'specific_location',
          'activity_at_time', 'body_parts', 'prevention_suggestion', 'saw_doctor', 'doctor_name', 'doctor_phone', 'prior_injury',
          'injury_occurred', 'ems_called', 'ems_arrival_time', 'police_called', 'police_report_no'],
      };
      [...base, ...byType[type]].forEach(k => { if (f[k] !== '' && f[k] != null) fd.append(k, f[k]); });
      if (photo) { const c = await compressImage(photo).catch(() => photo); fd.append('photo', c, photo.name || 'photo.jpg'); }
      const res = await fetch(`${API}/api/incidents`, { method: 'POST', credentials: 'include', body: fd });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not submit report.');
      toast.success('Incident report submitted.');
      onSaved();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const emergencyBlock = (
    <div className="border-t border-gray-700 pt-4 space-y-3">
      <div className="text-cream text-sm font-bold">Injury & Emergency Response</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 [&>div]:min-w-0">
        <div><label className={LBL}>Injury occurred?</label><YesNo value={f.injury_occurred} onChange={v => set('injury_occurred', v)} /></div>
        <div><label className={LBL}>EMS called?</label><YesNo value={f.ems_called} onChange={v => set('ems_called', v)} /></div>
        {f.ems_called === 'true' && <div><label className={LBL}>EMS arrival time</label><input className={FIELD} value={f.ems_arrival_time} onChange={e => set('ems_arrival_time', e.target.value)} /></div>}
        <div><label className={LBL}>Police called?</label><YesNo value={f.police_called} onChange={v => set('police_called', v)} /></div>
        {f.police_called === 'true' && <div><label className={LBL}>Police report / case #</label><input className={FIELD} value={f.police_report_no} onChange={e => set('police_report_no', e.target.value)} /></div>}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <h3 className="text-white font-semibold text-lg">Report an Incident</h3>

        <div>
          <label className={LBL}>Report type</label>
          <div className="flex flex-wrap gap-2">
            {REPORT_TYPES.map(t => (
              <button key={t.key} type="button" onClick={() => setType(t.key)}
                className={`px-3 py-1.5 rounded-lg text-sm border transition ${type === t.key ? 'bg-orange-500 text-white border-orange-500' : 'bg-gray-900 text-gray-300 border-gray-700 hover:border-gray-500'}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 [&>div]:min-w-0">
          <div><label className={LBL}>Date</label><input type="date" className={FIELD} value={f.incident_date} onChange={e => set('incident_date', e.target.value)} /></div>
          <div><label className={LBL}>Time <span className="normal-case text-gray-600">(optional)</span></label><input className={FIELD} value={f.incident_time} onChange={e => set('incident_time', e.target.value)} placeholder="e.g. 2:30 PM" /></div>
          <div><label className={LBL}>Location</label><select className={FIELD} value={f.location} onChange={e => set('location', e.target.value)}><option value="">Select…</option>{LOCATIONS.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}</select></div>
          {type === 'general' && <div><label className={LBL}>Category</label><select className={FIELD} value={f.category} onChange={e => set('category', e.target.value)}><option value="">Select…</option>{GENERAL_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}</select></div>}
        </div>

        <div><label className={LBL}>What happened? <span className="text-red-400">*</span></label><textarea rows={4} className={FIELD} value={f.description} onChange={e => set('description', e.target.value)} placeholder="Describe the incident, step by step." /></div>
        <div><label className={LBL}>Witnesses <span className="normal-case text-gray-600">(optional)</span></label><input className={FIELD} value={f.witnesses} onChange={e => set('witnesses', e.target.value)} /></div>

        {/* Customer section */}
        {type === 'customer' && (
          <div className="border-t border-gray-700 pt-4 space-y-3">
            <div className="text-cream text-sm font-bold">Customer Info</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 [&>div]:min-w-0">
              <div><label className={LBL}>Customer name</label><input className={FIELD} value={f.customer_name} onChange={e => set('customer_name', e.target.value)} /></div>
              <div><label className={LBL}>Phone</label><input className={FIELD} value={f.customer_phone} onChange={e => set('customer_phone', e.target.value)} /></div>
              <div><label className={LBL}>Email</label><input className={FIELD} value={f.customer_email} onChange={e => set('customer_email', e.target.value)} /></div>
              <div><label className={LBL}>Receipt / Order #</label><input className={FIELD} value={f.receipt_order_no} onChange={e => set('receipt_order_no', e.target.value)} /></div>
              <div><label className={LBL}>Payment method</label><input className={FIELD} value={f.payment_method} onChange={e => set('payment_method', e.target.value)} placeholder="cash, card…" /></div>
            </div>
          </div>
        )}

        {/* Employee section */}
        {type === 'employee' && (
          <div className="border-t border-gray-700 pt-4 space-y-3">
            <div className="text-cream text-sm font-bold">Employee Details</div>
            <div>
              <label className={LBL}>This is a report of a…</label>
              <div className="flex flex-wrap gap-2">
                {MATTER_TYPES.map(m => (
                  <button key={m.key} type="button" onClick={() => set('employee_matter_type', m.key)}
                    className={`px-3 py-1.5 rounded-lg text-sm border transition ${f.employee_matter_type === m.key ? 'bg-orange-500 text-white border-orange-500' : 'bg-gray-900 text-gray-300 border-gray-700 hover:border-gray-500'}`}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 [&>div]:min-w-0">
              <div><label className={LBL}>Job title</label><input className={FIELD} value={f.job_title} onChange={e => set('job_title', e.target.value)} /></div>
              <div><label className={LBL}>Supervisor</label><input className={FIELD} value={f.supervisor_name} onChange={e => set('supervisor_name', e.target.value)} /></div>
              <div><label className={LBL}>Told your supervisor?</label><YesNo value={f.supervisor_notified} onChange={v => set('supervisor_notified', v)} /></div>
              <div><label className={LBL}>Where exactly did it happen?</label><input className={FIELD} value={f.specific_location} onChange={e => set('specific_location', e.target.value)} /></div>
            </div>
            <div><label className={LBL}>What were you doing at the time?</label><textarea rows={2} className={FIELD} value={f.activity_at_time} onChange={e => set('activity_at_time', e.target.value)} /></div>
            <div><label className={LBL}>Part(s) of body injured / how you could've been hurt</label><input className={FIELD} value={f.body_parts} onChange={e => set('body_parts', e.target.value)} /></div>
            <div><label className={LBL}>What could have prevented this?</label><textarea rows={2} className={FIELD} value={f.prevention_suggestion} onChange={e => set('prevention_suggestion', e.target.value)} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 [&>div]:min-w-0">
              <div><label className={LBL}>Saw a doctor?</label><YesNo value={f.saw_doctor} onChange={v => set('saw_doctor', v)} /></div>
              <div><label className={LBL}>Injured this part before?</label><YesNo value={f.prior_injury} onChange={v => set('prior_injury', v)} /></div>
              {f.saw_doctor === 'true' && <div><label className={LBL}>Doctor's name</label><input className={FIELD} value={f.doctor_name} onChange={e => set('doctor_name', e.target.value)} /></div>}
              {f.saw_doctor === 'true' && <div><label className={LBL}>Doctor's phone</label><input className={FIELD} value={f.doctor_phone} onChange={e => set('doctor_phone', e.target.value)} /></div>}
            </div>
          </div>
        )}

        {/* People involved (general/customer) */}
        {type !== 'employee' && <div><label className={LBL}>People involved <span className="normal-case text-gray-600">(optional)</span></label><input className={FIELD} value={f.people_involved} onChange={e => set('people_involved', e.target.value)} /></div>}

        {(type === 'customer' || type === 'employee') && emergencyBlock}

        <div><label className={LBL}>Immediate action taken <span className="normal-case text-gray-600">(optional)</span></label><textarea rows={2} className={FIELD} value={f.action_taken} onChange={e => set('action_taken', e.target.value)} /></div>
        <div><label className={LBL}>Anything else <span className="normal-case text-gray-600">(optional)</span></label><textarea rows={2} className={FIELD} value={f.additional_notes} onChange={e => set('additional_notes', e.target.value)} /></div>
        <div>
          <label className={LBL}>Photo <span className="normal-case text-gray-600">(optional)</span></label>
          <input type="file" accept="image/*" onChange={e => setPhoto(e.target.files?.[0] || null)}
            className="block w-full max-w-full text-gray-300 text-sm file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border-0 file:bg-gray-700 file:text-gray-200 file:text-sm" />
          {photo && <p className="text-gray-500 text-xs mt-1 break-all">{photo.name}</p>}
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-300 hover:text-white transition">Cancel</button>
          <button onClick={submit} disabled={saving} className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: '#F05A28' }}>
            {saving ? 'Submitting…' : 'Submit Report'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail / staged review ────────────────────────────────────────────────────
function DetailModal({ id, onClose, onChanged }) {
  const toast = useToast();
  const [r, setR] = useState(null);
  const [inv, setInv] = useState({});
  const [hr, setHr] = useState({});
  const [status, setStatus] = useState('open');
  const [resolution, setResolution] = useState('');
  const [savingInv, setSavingInv] = useState(false);
  const [savingHr, setSavingHr] = useState(false);

  const load = useCallback(() => {
    fetch(`${API}/api/incidents/${id}`, { credentials: 'include' }).then(x => x.ok ? x.json() : null).then(d => {
      if (!d) return;
      setR(d); setStatus(d.status); setResolution(d.resolution_notes || '');
      setInv({
        injured_dob: d.injured_dob || '', injured_phone: d.injured_phone || '', injured_address: d.injured_address || '',
        injured_sex: d.injured_sex || '', inv_cause: d.inv_cause || '', inv_safety_regs: d.inv_safety_regs || '',
        inv_corrective_action: d.inv_corrective_action || '', doctor_name: d.doctor_name || '', doctor_phone: d.doctor_phone || '',
        hospital_name: d.hospital_name || '', inv_notes: d.inv_notes || '',
      });
      setHr({
        hr_severity: d.hr_severity || '', hr_body_part: d.hr_body_part || '', hr_injury_nature: d.hr_injury_nature || '',
        hr_cause: d.hr_cause || '', hr_prevention_plan: d.hr_prevention_plan || '',
      });
    }).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const saveInv = async () => {
    setSavingInv(true);
    try {
      const res = await fetch(`${API}/api/incidents/${id}/investigation`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(inv),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not save.');
      toast.success('Investigation saved.'); onChanged();
    } catch (e) { toast.error(e.message); } finally { setSavingInv(false); }
  };
  const saveHr = async () => {
    setSavingHr(true);
    try {
      const res = await fetch(`${API}/api/incidents/${id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ status, resolution_notes: resolution, ...hr }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not save.');
      toast.success('Saved.'); onChanged();
    } catch (e) { toast.error(e.message); } finally { setSavingHr(false); }
  };
  const del = async () => {
    if (!window.confirm('Delete this incident report permanently?')) return;
    try {
      const res = await fetch(`${API}/api/incidents/${id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error();
      toast.success('Report deleted.'); onChanged();
    } catch { toast.error('Delete failed.'); }
  };

  const setI = (k, v) => setInv(p => ({ ...p, [k]: v }));
  const setH = (k, v) => setHr(p => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-xl border border-gray-700 w-full max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700 sticky top-0 bg-gray-800 z-10">
          <h3 className="text-white font-semibold">Incident Report</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl leading-none">×</button>
        </div>
        {!r ? <p className="text-gray-500 text-sm text-center py-10">Loading…</p> : (
          <div className="p-4 space-y-4">
            {/* Report */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">{TYPE_LABEL[r.report_type] || r.report_type}</span>
              {r.location && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">{LOC_LABEL[r.location] || r.location}</span>}
            </div>
            <Field label="When">{fmtDate(r.incident_date)}{r.incident_time ? ` · ${r.incident_time}` : ''}</Field>
            {r.report_type === 'general' && <Field label="Category">{CAT_LABEL[r.category] || r.category}</Field>}
            {r.report_type === 'employee' && <Field label="Type">{MATTER_LABEL[r.employee_matter_type] || r.employee_matter_type}</Field>}
            <Field label="What happened">{r.description}</Field>
            <Field label="Witnesses">{r.witnesses}</Field>
            <Field label="People involved">{r.people_involved}</Field>
            <Field label="Immediate action taken">{r.action_taken}</Field>

            {r.report_type === 'customer' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Customer">{r.customer_name}</Field>
                <Field label="Phone">{r.customer_phone}</Field>
                <Field label="Email">{r.customer_email}</Field>
                <Field label="Receipt / Order #">{r.receipt_order_no}</Field>
                <Field label="Payment">{r.payment_method}</Field>
              </div>
            )}
            {r.report_type === 'employee' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Job title">{r.job_title}</Field>
                <Field label="Supervisor">{r.supervisor_name}</Field>
                <Field label="Told supervisor">{yesNo(r.supervisor_notified)}</Field>
                <Field label="Where exactly">{r.specific_location}</Field>
                <Field label="Doing at the time">{r.activity_at_time}</Field>
                <Field label="Body part(s)">{r.body_parts}</Field>
                <Field label="Could've been prevented by">{r.prevention_suggestion}</Field>
                <Field label="Saw doctor">{yesNo(r.saw_doctor)}</Field>
                <Field label="Injured before">{yesNo(r.prior_injury)}</Field>
              </div>
            )}
            {(r.injury_occurred != null || r.ems_called != null || r.police_called != null) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Injury occurred">{yesNo(r.injury_occurred)}</Field>
                <Field label="EMS called">{yesNo(r.ems_called)}{r.ems_arrival_time ? ` · ${r.ems_arrival_time}` : ''}</Field>
                <Field label="Police called">{yesNo(r.police_called)}{r.police_report_no ? ` · #${r.police_report_no}` : ''}</Field>
              </div>
            )}
            <Field label="Anything else">{r.additional_notes}</Field>
            {r.has_photo && <div><div className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-1">Photo</div><IncidentPhoto id={id} /></div>}
            <Field label="Reported by">{r.reported_by_name} · {fmtDateTime(r.created_at)}</Field>

            {/* Stage 2 — Supervisor Investigation */}
            {(r.can_investigate || r.inv_at) && (
              <div className="border-t border-gray-700 pt-4 space-y-3">
                <div className="text-cream text-sm font-bold">Supervisor Investigation{r.inv_by_name ? <span className="text-gray-500 font-normal"> · {r.inv_by_name}{r.inv_at ? ` · ${fmtDateTime(r.inv_at)}` : ''}</span> : ''}</div>
                {r.can_investigate ? (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 [&>div]:min-w-0">
                      <div><label className={LBL}>Injured person DOB</label><input type="date" className={FIELD} value={inv.injured_dob} onChange={e => setI('injured_dob', e.target.value)} /></div>
                      <div><label className={LBL}>Phone</label><input className={FIELD} value={inv.injured_phone} onChange={e => setI('injured_phone', e.target.value)} /></div>
                      <div className="sm:col-span-2"><label className={LBL}>Address</label><input className={FIELD} value={inv.injured_address} onChange={e => setI('injured_address', e.target.value)} /></div>
                      <div><label className={LBL}>Sex</label><input className={FIELD} value={inv.injured_sex} onChange={e => setI('injured_sex', e.target.value)} /></div>
                    </div>
                    <div><label className={LBL}>What caused the event?</label><textarea rows={2} className={FIELD} value={inv.inv_cause} onChange={e => setI('inv_cause', e.target.value)} /></div>
                    <div><label className={LBL}>Were safety regs in place / used? If not, what was wrong?</label><textarea rows={2} className={FIELD} value={inv.inv_safety_regs} onChange={e => setI('inv_safety_regs', e.target.value)} /></div>
                    <div><label className={LBL}>Recommended corrective / preventive action</label><textarea rows={2} className={FIELD} value={inv.inv_corrective_action} onChange={e => setI('inv_corrective_action', e.target.value)} /></div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 [&>div]:min-w-0">
                      <div><label className={LBL}>Doctor's name</label><input className={FIELD} value={inv.doctor_name} onChange={e => setI('doctor_name', e.target.value)} /></div>
                      <div><label className={LBL}>Hospital name</label><input className={FIELD} value={inv.hospital_name} onChange={e => setI('hospital_name', e.target.value)} /></div>
                    </div>
                    <div><label className={LBL}>Investigation notes</label><textarea rows={2} className={FIELD} value={inv.inv_notes} onChange={e => setI('inv_notes', e.target.value)} /></div>
                    <div className="flex justify-end"><button onClick={saveInv} disabled={savingInv} className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: '#F05A28' }}>{savingInv ? 'Saving…' : 'Save Investigation'}</button></div>
                  </>
                ) : (
                  <div className="space-y-2">
                    <Field label="Cause">{r.inv_cause}</Field>
                    <Field label="Safety regs">{r.inv_safety_regs}</Field>
                    <Field label="Corrective action">{r.inv_corrective_action}</Field>
                    <Field label="Doctor / Hospital">{[r.doctor_name, r.hospital_name].filter(Boolean).join(' · ')}</Field>
                    <Field label="Notes">{r.inv_notes}</Field>
                  </div>
                )}
              </div>
            )}

            {/* Stage 3 — HR Follow-up (HR/admin only) */}
            {r.can_manage ? (
              <div className="border-t border-gray-700 pt-4 space-y-3">
                <div className="text-cream text-sm font-bold">HR Follow-up{r.hr_by_name ? <span className="text-gray-500 font-normal"> · {r.hr_by_name}{r.hr_at ? ` · ${fmtDateTime(r.hr_at)}` : ''}</span> : ''}</div>
                <div><label className={LBL}>Severity</label><select className={FIELD} value={hr.hr_severity} onChange={e => setH('hr_severity', e.target.value)}><option value="">Select…</option>{HR_SEVERITY.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}</select></div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 [&>div]:min-w-0">
                  <div><label className={LBL}>Part of body</label><input className={FIELD} value={hr.hr_body_part} onChange={e => setH('hr_body_part', e.target.value)} /></div>
                  <div><label className={LBL}>Nature of injury</label><input className={FIELD} value={hr.hr_injury_nature} onChange={e => setH('hr_injury_nature', e.target.value)} /></div>
                </div>
                <div><label className={LBL}>Root cause</label><textarea rows={2} className={FIELD} value={hr.hr_cause} onChange={e => setH('hr_cause', e.target.value)} /></div>
                <div><label className={LBL}>Prevention plan</label><textarea rows={2} className={FIELD} value={hr.hr_prevention_plan} onChange={e => setH('hr_prevention_plan', e.target.value)} /></div>
                <div className="border-t border-gray-700 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 [&>div]:min-w-0">
                  <div><label className={LBL}>Status</label><select className={FIELD} value={status} onChange={e => setStatus(e.target.value)}>{STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}</select></div>
                  <div><label className={LBL}>Resolution notes</label><input className={FIELD} value={resolution} onChange={e => setResolution(e.target.value)} /></div>
                </div>
                <div className="flex gap-2 justify-between flex-wrap">
                  <button onClick={del} className="px-4 py-2 rounded-lg text-sm text-red-400 hover:text-red-300 transition">Delete</button>
                  <button onClick={saveHr} disabled={savingHr} className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: '#F05A28' }}>{savingHr ? 'Saving…' : 'Save HR Follow-up'}</button>
                </div>
              </div>
            ) : r.hr_at ? (
              <div className="border-t border-gray-700 pt-4 space-y-2">
                <div className="text-cream text-sm font-bold">HR Follow-up</div>
                <Field label="Severity">{SEVERITY_LABEL[r.hr_severity] || r.hr_severity}</Field>
                <Field label="Prevention plan">{r.hr_prevention_plan}</Field>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function IncidentReports({ user, onBack, focus }) {
  const toast = useToast();
  const [data, setData] = useState({ can_manage: false, overseen: [], reports: [] });
  const [loading, setLoading] = useState(true);
  const [filing, setFiling] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  // Deep-link: a notification url like /incident-reports?id=45 opens that report.
  // The detail modal fetches by id, so we just set it once.
  const didFocus = useRef(false);
  useEffect(() => {
    if (didFocus.current || !focus?.id) return;
    didFocus.current = true;
    setDetailId(Number(focus.id));
  }, [focus]);

  const canManage = data.can_manage;
  const isReviewer = canManage || (data.overseen && data.overseen.length > 0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : '';
      const res = await fetch(`${API}/api/incidents${qs}`, { credentials: 'include' });
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch { toast.error('Failed to load reports.'); } finally { setLoading(false); }
  }, [statusFilter, toast]);
  useEffect(() => { load(); }, [load]);

  const reports = data.reports || [];

  return (
    <div className="min-h-screen bg-gray-900">
      <TopNav />

      <div className="px-4 sm:px-6 py-8 sm:py-10 max-w-4xl mx-auto">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between mb-8">
          <div>
            <h1 className="text-cream text-3xl font-bold">Incident Reports</h1>
            <p className="text-gray-400 text-sm mt-2">
              {canManage ? 'Review, investigate, and resolve incidents.'
                : isReviewer ? "Your team's reports, plus your own." : 'File a report and track your own submissions.'}
            </p>
          </div>
          <button onClick={() => setFiling(true)} className="px-4 py-2 rounded-lg text-white text-sm font-semibold shrink-0 self-start" style={{ backgroundColor: '#F05A28' }}>+ Report an Incident</button>
        </div>

        {canManage && (
          <div className="flex flex-wrap gap-2 mb-5">
            <button onClick={() => setStatusFilter('')} className={`px-3 py-1.5 rounded-lg text-sm transition ${statusFilter === '' ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>All</button>
            {STATUSES.map(s => <button key={s.key} onClick={() => setStatusFilter(s.key)} className={`px-3 py-1.5 rounded-lg text-sm transition ${statusFilter === s.key ? 'bg-orange-500 text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}>{s.label}</button>)}
          </div>
        )}

        {loading ? <p className="text-gray-500 text-sm text-center py-12">Loading…</p>
          : reports.length === 0 ? <p className="text-gray-500 text-sm text-center py-12">{isReviewer ? 'No incident reports.' : "You haven't filed any reports yet."}</p>
          : (
            <div className="space-y-2">
              {reports.map(r => (
                <button key={r.id} onClick={() => setDetailId(r.id)} className="w-full text-left bg-gray-800 border border-gray-700 rounded-xl p-4 hover:border-gray-500 transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${STATUS_BADGE[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-300">{TYPE_LABEL[r.report_type] || r.report_type}</span>
                        {r.has_investigation && <span className="text-xs px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-300 border border-sky-500/30">Investigated</span>}
                        {r.has_hr_review && <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300 border border-violet-500/30">HR</span>}
                        {r.location && <span className="text-xs text-gray-500">{LOC_LABEL[r.location] || r.location}</span>}
                      </div>
                      <div className="text-white text-sm line-clamp-2">{r.description}</div>
                      <div className="text-gray-500 text-xs mt-1">
                        {fmtDate(r.incident_date)}{isReviewer ? ` · ${r.reported_by_name}` : ''}{r.has_photo ? ' · 📎' : ''}
                      </div>
                    </div>
                    <span className="text-gray-500 text-lg flex-shrink-0">›</span>
                  </div>
                </button>
              ))}
            </div>
          )}
      </div>

      {filing && <ReportModal user={user} onClose={() => setFiling(false)} onSaved={() => { setFiling(false); load(); }} />}
      {detailId && <DetailModal id={detailId} onClose={() => setDetailId(null)} onChanged={() => { setDetailId(null); load(); }} />}
    </div>
  );
}
